import Section from '@/components/common/Section';
// ...existing code...
import { motion } from 'framer-motion';
import { fadeUp, staggerContainer } from '@/animations/variants';

const services = [
	{
		title: 'Product Engineering',
		description:
			'Transform ideas into scalable platforms with comprehensive product development from concept to market launch.',
		icon: (
			<img
				src="/icons/Gear.gif"
				alt="Product Engineering"
				className="w-40 h-40 object-contain"
			/>
		), // updated to use Gear.gif
		features: [
			'Full-stack development',
			'Architecture design',
			'Performance optimization',
			'Security implementation',
			'Scalability planning',
		],
	},
	{
		title: 'Enterprise Automation',
		description:
			'Modernize legacy systems with intelligent automation, workflow optimization, and system integration.',
		icon: (
			<img
				src="/icons/Artificially intelligent robot Cooperation.gif"
				alt="Enterprise Automation"
				className="w-40 h-40 object-contain"
			/>
		), // updated to use gif
		features: [
			'Legacy system modernization',
			'Workflow automation',
			'System integration',
			'Business process optimization',
			'Data migration',
		],
	},
	{
		title: 'Intelligent Systems',
		description:
			'Build AI-powered solutions that enhance decision-making and operational efficiency across your organization.',
		icon: (
			<img
				src="/icons/Artificial Intelligence.gif"
				alt="Intelligent Systems"
				className="w-40 h-40 object-contain"
			/>
		), // updated to use gif
		features: [
			'AI/ML model development',
			'Natural language processing',
			'Computer vision solutions',
			'Predictive analytics',
			'Intelligent automation',
		],
	},
	{
		title: 'AI-enabled Platforms',
		description:
			'Develop next-generation platforms powered by artificial intelligence and machine learning capabilities.',
		icon: (
			<img
				src="/icons/platform.gif"
				alt="AI-enabled Platforms"
				className="w-40 h-40 object-contain"
			/>
		), // updated to use gif
		features: [
			'AI platform architecture',
			'Machine learning pipelines',
			'Data processing systems',
			'Model deployment',
			'AI-driven insights',
		],
	},
];

const deliveryApproach = [
	{
		title: 'Discovery & Planning',
		description: 'Comprehensive requirement analysis and technical architecture design.',
		icon: '🔍',
	},
	{
		title: 'Agile Development',
		description: 'Iterative development with continuous feedback and rapid deployment cycles.',
		icon: '🚀',
	},
	{
		title: 'Quality Assurance',
		description: 'Rigorous testing, code review, and performance optimization processes.',
		icon: '✅',
	},
	{
		title: 'Deployment & Support',
		description: 'Seamless deployment with ongoing maintenance and optimization support.',
		icon: '🔧',
	},
];

export default function ServicesPage() {
	return (
		<>
			{/* Services Overview */}
			<Section
				title="Our Services"
				subtitle="End-to-end software development with architectural control and long-term ownership"
				centered
				background="gradient"
			>
				<motion.p
					className="text-lg text-textSecondary max-w-4xl mx-auto text-center mb-12"
					variants={fadeUp}
				>
					We specialize in transforming complex business challenges into elegant software solutions.
					Our comprehensive services cover the entire software development lifecycle, from initial
					concept to ongoing optimization and scaling.
				</motion.p>
			</Section>

			{/* Main Services */}
			<Section>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
					{services.map((service, index) => (
						<motion.div
							key={index}
							className="bg-surface border border-border rounded-xl p-8 hover:border-primary/50 transition-all duration-normal"
							variants={fadeUp}
							whileHover={{
								y: -5,
								boxShadow: '0px 20px 40px rgba(0,0,0,0.15)',
							}}
						>
							<div className="flex items-start mb-6">
								<div className="text-4xl mr-4">{service.icon}</div>
								<div>
									<h3 className="text-2xl font-heading font-semibold text-textPrimary mb-3">
										{service.title}
									</h3>
									<p className="text-textSecondary leading-relaxed">
										{service.description}
									</p>
								</div>
							</div>

							<div className="space-y-2">
								<h4 className="text-sm font-medium text-textPrimary uppercase tracking-wider mb-3">
									Key Capabilities
								</h4>
								<div className="flex flex-wrap gap-2">
									{service.features.map((feature, featureIndex) => (
										<span
											key={featureIndex}
											className="text-xs px-3 py-1 bg-primary/10 text-primary rounded-full"
										>
											{feature}
										</span>
									))}
								</div>
							</div>
						</motion.div>
					))}
				</div>
			</Section>

			{/* Delivery Approach */}
			<Section
				title="Our Delivery Approach"
				subtitle="Structured methodology that ensures successful project outcomes"
				centered
				background="surface"
			>
				<motion.div
					className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
					variants={staggerContainer}
				>
					{deliveryApproach.map((step, index) => (
						<motion.div key={index} variants={fadeUp}>
							<div className="text-center">
								<div className="relative mb-6">
									<div className="w-24 h-24 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
										<span className="text-4xl">{step.icon}</span>
									</div>
									<div className="absolute -top-2 -right-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm font-bold">
										{index + 1}
									</div>
								</div>
								<h3 className="text-lg font-heading font-semibold text-textPrimary mb-3">
									{step.title}
								</h3>
								<p className="text-sm text-textSecondary">
									{step.description}
								</p>
							</div>
						</motion.div>
					))}
				</motion.div>
			</Section>

			{/* Delivery Statement */}
			<Section centered>
				<motion.div
					className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-xl p-8 md:p-12 text-center"
					variants={fadeUp}
				>
					<h2 className="text-2xl md:text-3xl font-heading font-semibold text-textPrimary mb-4">
						End-to-End Ownership with Architectural Control
					</h2>
					<p className="text-lg text-textSecondary max-w-3xl mx-auto">
						We take complete responsibility for your project's success, from initial architecture
						decisions to final deployment and ongoing optimization. Our team maintains full
						ownership of code quality, performance, and scalability throughout the development
						lifecycle.
					</p>
				</motion.div>
			</Section>

			{/* Call to Action */}
			<Section centered background="surface">
				<motion.div
					className="text-center"
					variants={fadeUp}
				>
					<h2 className="text-3xl md:text-4xl font-heading font-semibold text-textPrimary mb-6">
						Ready to Start Your Project?
					</h2>
					<p className="text-lg text-textSecondary mb-8 max-w-2xl mx-auto">
						Let's discuss your requirements and explore how our services can help you
						achieve your software development goals.
					</p>
					<div className="flex flex-col sm:flex-row gap-4 justify-center">
						<button className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-heading font-semibold text-lg shadow-lg hover:shadow-glow transition-all duration-normal">
							Discuss Your Project
						</button>
						<button className="border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-4 rounded-lg font-heading font-semibold text-lg transition-all duration-normal">
							View Case Studies
						</button>
					</div>
				</motion.div>
			</Section>
		</>
	);
}